import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../misc/Tabs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { IoShareSocial } from "react-icons/io5";
import { FaRegComment } from "react-icons/fa";
import { AiOutlineLike } from "react-icons/ai";
import { PostFooterInfoProps, SocialPlatforms } from "./types";
import { X } from "lucide-react";
import CustomDialog from "../misc/CustomDialog";

const PostFooterInfo: React.FC<PostFooterInfoProps> = ({
    openInfoDialog,
    setOpenInfoDialog,
    parsedFooter,
    platformName,
}) => {
    const user = useSelector((state: RootState) => state.auth.user);

    return (
        <CustomDialog
            open={openInfoDialog}
            onClose={() => setOpenInfoDialog(false)}
            className="max-w-4xl w-full"
        >
            <div className="max-w-4xl min-w-4xl bg-white">
                <h2 className="text-xl font-bold mb-4 text-primary text-center">
                    Footer Placement Examples
                </h2>
                <button
                    className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
                    onClick={() => setOpenInfoDialog(false)}
                >
                    <X className="h-6 w-6" />
                </button>
                <Tabs
                    defaultValue={
                        platformName === SocialPlatforms.Instagram
                            ? "comment"
                            : "post"
                    }
                    className="w-full"
                >
                    <TabsList
                        className={`grid w-full grid-cols-${
                            platformName !== SocialPlatforms.Pinterest &&
                            platformName !== SocialPlatforms.Google &&
                            platformName !== SocialPlatforms.Instagram &&
                            platformName !== SocialPlatforms.Twitter
                                ? "2"
                                : "1"
                        }`}
                    >
                        {platformName !== SocialPlatforms.Instagram &&
                            platformName !== SocialPlatforms.Twitter && (
                                <TabsTrigger value="post">
                                    In Post Content
                                </TabsTrigger>
                            )}
                        {platformName !== SocialPlatforms.Pinterest &&
                            platformName !== SocialPlatforms.Google && (
                                <TabsTrigger value="comment">
                                    As First Comment
                                </TabsTrigger>
                            )}
                    </TabsList>
                    <TabsContent value="post" className="mt-4">
                        <div className="border rounded-lg p-4 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="font-semibold">
                                        {user?.nameFirst.charAt(0)}
                                        {user?.nameLast.charAt(0)}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-semibold">
                                        {user?.nameFirst} {user?.nameLast}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        2 hours ago
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm text-muted-foreground">
                                <p>
                                    This is where your social media caption or
                                    marketing message will appear when your post
                                    goes live.
                                </p>
                            </div>
                            <div className="mt-3 pt-3 text-sm text-muted-foreground">
                                {parsedFooter
                                    ?.split("\n")
                                    .map((line, index) =>
                                        line.trim() === "" ? (
                                            <div key={index} className="h-4" />
                                        ) : (
                                            <p key={index}>{line}</p>
                                        )
                                    )}
                            </div>
                            <div className="flex gap-4 pt-2">
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <AiOutlineLike className="h-4 w-4" />
                                    Like
                                </button>
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <FaRegComment className="h-4 w-4" />
                                    Comment
                                </button>
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <IoShareSocial className="h-4 w-4" />
                                    Share
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 text-left">
                            The footer is added to the post content so it’s
                            immediately visible.
                        </p>
                    </TabsContent>
                    <TabsContent value="comment" className="mt-4">
                        <div className="border rounded-lg p-4 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="font-semibold">
                                        {user?.nameFirst.charAt(0)}
                                        {user?.nameLast.charAt(0)}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-semibold">
                                        {user?.nameFirst} {user?.nameLast}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        2 hours ago
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm text-muted-foreground">
                                <p>
                                    This is where your social media caption or
                                    marketing message will appear when your post
                                    goes live.
                                </p>
                            </div>
                            <div className="flex gap-4 pt-2">
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <AiOutlineLike className="h-4 w-4" />
                                    Like
                                </button>
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <FaRegComment className="h-4 w-4" />
                                    Comment
                                </button>
                                <button className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <IoShareSocial className="h-4 w-4" />
                                    Share
                                </button>
                            </div>
                            <div className="mt-4 pt-4 border-t">
                                <div className="flex gap-2">
                                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="font-semibold text-xs">
                                            {user?.nameFirst.charAt(0)}
                                            {user?.nameLast.charAt(0)}
                                        </span>
                                    </div>
                                    <div className="bg-gray-100 rounded-lg p-3 flex-1">
                                        <p className="font-semibold text-sm">
                                            {user?.nameFirst} {user?.nameLast}
                                        </p>
                                        <div className="text-sm text-muted-foreground">
                                            {parsedFooter
                                                ?.split("\n")
                                                .map((line, index) =>
                                                    line.trim() === "" ? (
                                                        <div
                                                            key={index}
                                                            className="h-4"
                                                        />
                                                    ) : (
                                                        <p key={index}>
                                                            {line}
                                                        </p>
                                                    )
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 text-left">
                            The footer is posted as the first comment to keep
                            the main post clean.
                        </p>
                        {platformName === SocialPlatforms.Instagram && (
                            <p className="text-sm text-muted-foreground mt-2 text-left">
                                Enter text to be sent as the first comment for
                                your Instagram post. Please refrain from
                                including the MLS number, phone numbers or
                                property address as there is a high probability
                                that Instagram will flag the comment as spam and
                                it will not be shown to your followers.
                            </p>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </CustomDialog>
    );
};

export default PostFooterInfo;
